// src/pages/relapse/TriggersPage.tsx - Trigger tags management page

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Fab,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { 
  fetchTriggerTags,
  createTriggerTag,
  updateTriggerTag,
  deleteTriggerTag
} from '../../store/slices/relapseSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const TriggersPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { triggers, isLoading, error } = useSelector((state: RootState) => state.relapse);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTriggerId, setEditingTriggerId] = useState<string | null>(null);
  const [triggerName, setTriggerName] = useState('');
  const [triggerDescription, setTriggerDescription] = useState('');
  const [triggerColor, setTriggerColor] = useState('#3F51B5');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    if (currentUser) {
      loadTriggers();
    }
  }, [currentUser]);
  
  const loadTriggers = async () => {
    if (currentUser) {
      await dispatch(fetchTriggerTags(currentUser.uid));
    }
  };
  
  const handleOpenAddDialog = () => {
    setEditingTriggerId(null);
    setTriggerName('');
    setTriggerDescription('');
    setTriggerColor('#3F51B5');
    setDialogOpen(true);
  };
  
  const handleOpenEditDialog = (trigger: any) => {
    setEditingTriggerId(trigger.id);
    setTriggerName(trigger.name);
    setTriggerDescription(trigger.description || '');
    setTriggerColor(trigger.color || '#3F51B5');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setShowColorPicker(false);
  };
  
  const handleSaveTrigger = async () => {
    if (!currentUser || !triggerName.trim()) return;
    
    const triggerData = {
      userId: currentUser.uid,
      name: triggerName.trim(),
      description: triggerDescription.trim(),
      color: triggerColor,
    };
    
    let result;
    
    if (editingTriggerId) {
      // Update existing trigger
      result = await dispatch(updateTriggerTag({
        userId: currentUser.uid,
        tagId: editingTriggerId,
        updates: triggerData
      }));
    } else {
      // Create new trigger
      result = await dispatch(createTriggerTag(triggerData));
    }
    
    if (
      (createTriggerTag.fulfilled.match(result)) || 
      (updateTriggerTag.fulfilled.match(result))
    ) {
      handleCloseDialog();
    }
  };
  
  const handleOpenDeleteDialog = (triggerId: string) => {
    setTriggerToDelete(triggerId);
    setDeleteDialogOpen(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTriggerToDelete(null);
  };
  
  const handleDeleteTrigger = async () => {
    if (!currentUser || !triggerToDelete) return;
    
    const result = await dispatch(deleteTriggerTag({
      userId: currentUser.uid,
      tagId: triggerToDelete
    }));
    
    if (deleteTriggerTag.fulfilled.match(result)) {
      handleCloseDeleteDialog();
    }
  };
  
  const handleBackToRelapse = () => {
    navigate('/relapse');
  };
  
  const renderEmptyState = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
      <Typography variant="h6" gutterBottom>
        No trigger tags defined
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Create trigger tags to categorize and track patterns in your relapse incidents
      </Typography>
      <Button 
        variant="contained" 
        startIcon={<AddIcon />}
        onClick={handleOpenAddDialog}
      >
        Add Trigger Tag
      </Button>
    </Box>
  );
  
  if (isLoading && triggers.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Manage Triggers
        </Typography>
        <Button 
          variant="outlined" 
          onClick={handleBackToRelapse}
        >
          Back to Tracker
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ width: '100%' }}>
        {triggers.length === 0 ? (
          renderEmptyState()
        ) : (
          <List>
            {triggers.map((trigger, index) => (
              <React.Fragment key={trigger.id}>
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton 
                        edge="end" 
                        aria-label="edit"
                        onClick={() => handleOpenEditDialog(trigger)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleOpenDeleteDialog(trigger.id!)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <Box 
                    sx={{ 
                      width: 16, 
                      height: 16, 
                      borderRadius: '50%', 
                      backgroundColor: trigger.color || '#3F51B5',
                      mr: 2
                    }} 
                  />
                  <ListItemText
                    primary={trigger.name}
                    secondary={trigger.description}
                  />
                </ListItem>
                {index < triggers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleOpenAddDialog}
      >
        <AddIcon />
      </Fab>
      
      {/* Add/Edit Trigger Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTriggerId ? 'Edit Trigger' : 'Add Trigger'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Trigger Name"
            fullWidth
            variant="outlined"
            value={triggerName}
            onChange={(e) => setTriggerName(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={triggerDescription}
            onChange={(e) => setTriggerDescription(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <Typography variant="subtitle1" gutterBottom>
            Color
          </Typography>
          
          <Box 
            sx={{ 
              height: 40, 
              backgroundColor: triggerColor,
              borderRadius: 1,
              cursor: 'pointer',
              mb: 2
            }}
            onClick={() => setShowColorPicker(!showColorPicker)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveTrigger} 
            variant="contained"
            disabled={!triggerName.trim() || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          Delete Trigger
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this trigger? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button 
            onClick={handleDeleteTrigger} 
            color="error"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TriggersPage;
